import { CodeNode } from '@lexical/code';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { TRANSFORMERS } from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import {
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  LexicalNode,
  ParagraphNode,
} from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ExcalidrawNode } from '../rich-lexical-editor/nodes/ExcalidrawNode';
import { ImageNode } from '../rich-lexical-editor/nodes/ImageNode';
import { KeywordNode } from '../rich-lexical-editor/nodes/KeywordNode';
import { MentionNode } from '../rich-lexical-editor/nodes/MentionNode';
import { YouTubeNode } from '../rich-lexical-editor/nodes/YouTubeNode';
import AutoLinkPlugin from '../rich-lexical-editor/plugins/AutoLinkPlugin';
import ExcalidrawPlugin from '../rich-lexical-editor/plugins/ExcalidrawPlugin';
import ImagePlugin from '../rich-lexical-editor/plugins/ImagePlugin';
import ListMaxIndentLevelPlugin from '../rich-lexical-editor/plugins/ListMaxIndentLevelPlugin';
import YouTubePlugin from '../rich-lexical-editor/plugins/YouTubePlugin';
import ExampleTheme from '../rich-lexical-editor/themes/ExampleTheme';
import styles from './basic-lexical-editor.module.less';
import ClipboardImagePlugin from './plugins/ClipboardImagePlugin';
import EnhancedClipboardImagePlugin from './plugins/EnhancedClipboardImagePlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import TablePlugin from './plugins/TablePlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';

// Base editor config - memoized per component instance
const createEditorConfig = () => ({
  theme: ExampleTheme,
  onError(error: Error) {
    throw error;
  },
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    ImageNode,
    YouTubeNode,
    MentionNode,
    KeywordNode,
    ExcalidrawNode,
  ],
});

interface ClipboardImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  useServerUpload?: boolean;
}

interface BasicLexicalEditorProps {
  value?: string;
  placeholder?: string;
  setValue: (value: string) => void;
  className?: string;
  target?: string;
  enableEnhancedClipboard?: boolean;
  clipboardImageOptions?: ClipboardImageOptions;
}

function DefaultValuePlugin({ defaultValue }: { defaultValue: string }) {
  const { t } = useTranslation('common');
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const currentHtml = $generateHtmlFromNodes(editor, null);
      if (currentHtml === defaultValue) {
        return;
      }

      try {
        const parser = new DOMParser();
        const dom = parser.parseFromString(defaultValue, 'text/html');

        // Generate nodes using standard Lexical method
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();

        // Post-process nodes to apply inline styles
        // Create temp div once outside the loop
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = defaultValue;
        const spans = tempDiv.querySelectorAll('span[style]');
        const spanStyles = new Map<string, string>();

        // Pre-process all span styles
        spans.forEach((span: Element) => {
          const htmlSpan = span as HTMLElement;
          const stylesToApply: string[] = [];

          // Collect all styles to apply
          if (htmlSpan.style.fontSize) {
            stylesToApply.push(`font-size: ${htmlSpan.style.fontSize}`);
          }
          if (htmlSpan.style.color) {
            stylesToApply.push(`color: ${htmlSpan.style.color}`);
          }
          if (htmlSpan.style.backgroundColor) {
            stylesToApply.push(
              `background-color: ${htmlSpan.style.backgroundColor}`
            );
          }
          if (htmlSpan.style.lineHeight) {
            stylesToApply.push(`line-height: ${htmlSpan.style.lineHeight}`);
          }
          if (htmlSpan.style.display) {
            stylesToApply.push(`display: ${htmlSpan.style.display}`);
          }

          if (stylesToApply.length > 0) {
            spanStyles.set(
              htmlSpan.textContent || '',
              stylesToApply.join('; ')
            );
          }
        });

        // Apply styles to nodes
        const nodesToAppend: LexicalNode[] = [];
        let currentParagraph: ParagraphNode | null = null;

        nodes.forEach((node: LexicalNode) => {
          // Handle text/inline nodes by wrapping them in a paragraph
          if ($isTextNode(node) || $isLineBreakNode(node)) {
            if (!currentParagraph) {
              currentParagraph = $createParagraphNode();
              nodesToAppend.push(currentParagraph);
            }

            // Apply styles if it's a text node
            if ($isTextNode(node)) {
              const textContent = node.getTextContent();
              const style = spanStyles.get(textContent);
              if (style) {
                node.setStyle(style);
              }
            }

            currentParagraph.append(node);
          } else {
            // If we hit a block node, reset the current paragraph
            currentParagraph = null;

            // Handle children of block nodes for styling
            if ($isElementNode(node)) {
              const children = node.getChildren();
              children.forEach((child: LexicalNode) => {
                if ($isTextNode(child)) {
                  const textContent = child.getTextContent();
                  const style = spanStyles.get(textContent);
                  if (style) {
                    child.setStyle(style);
                  }
                }
              });
            }
            nodesToAppend.push(node);
          }
        });

        root.append(...nodesToAppend);

        // Only set selection to end if the editor is currently focused
        // This prevents auto-scrolling when the value is updated externally (e.g. via AI suggestion accept)
        const rootElement = editor.getRootElement();
        if (rootElement?.contains(document.activeElement)) {
          root.selectEnd();
        }

        // Clean up temp DOM element
        tempDiv.remove();
      } catch (error) {
        console.warn(t('invalid_text_format_detected'));
      }
    });
  }, [defaultValue, editor, t]);

  return null;
}

export function BasicLexicalEditor({
  value,
  placeholder,
  setValue,
  className,
  target,
  enableEnhancedClipboard = false,
  clipboardImageOptions = {},
}: BasicLexicalEditorProps) {
  const { t } = useTranslation('common');

  // Memoize editor config to prevent recreation on every render
  const editorConfig = useMemo(() => createEditorConfig(), []);

  // Debounce setValue to reduce HTML generation frequency
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedSetValue = useCallback(
    (html: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setValue(html);
      }, 300); // 300ms debounce
    },
    [setValue]
  );

  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        editable: true,
        namespace: 'Editor',
        editorState: null,
      }}
    >
      <div className="editor-container-basic">
        <ToolbarPlugin target={target} setValue={setValue} />
        <div className={`editor-inner-basic editor-shell ${className}`}>
          <RichTextPlugin
            contentEditable={
              <div className={`editor-scroller-basic ${styles['scroller']}`}>
                <div className="editor">
                  <ContentEditable className="editor-input-basic" />
                </div>
              </div>
            }
            placeholder={
              <div className="editor-placeholder-basic">
                {placeholder || t('begin_writing_text')}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <DefaultValuePlugin defaultValue={value || ''} />
          <OnChangePlugin
            onChange={(editorState, editor) => {
              editor.update(() => {
                const html = $generateHtmlFromNodes(editor, null);
                debouncedSetValue(html);
              });
            }}
          />
          <ImagePlugin captionsEnabled={false} />
          {enableEnhancedClipboard ? (
            <EnhancedClipboardImagePlugin {...clipboardImageOptions} />
          ) : (
            <ClipboardImagePlugin />
          )}
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <YouTubePlugin />
          <TablePlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <FloatingTextFormatToolbarPlugin />
          <ExcalidrawPlugin />
        </div>
      </div>
    </LexicalComposer>
  );
}

export default BasicLexicalEditor;
